import { AsyncPipe, CommonModule, NgForOf, NgIf } from '@angular/common'
import { Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { BehaviorSubject, Observable, shareReplay, switchMap } from 'rxjs'
import { StatistiqueService, StatistiquesProjetFilters } from '../../data-access/http/statistique.service'
import {
  StatistiquesFiltresResponse,
  StatistiquesProjetResponse,
} from '../../data-access/http/dto/statistiques-projet-response'
import { UtilisateurDto } from '../../../utilisateur/dto/utilisateur-dto'
import { UtilisateursService } from '../../../utilisateur/service/utilisateurs.service'

type ChartMode = 'pie' | 'bar'

interface ChartItem {
  label: string
  value: number
  color: string
}

interface ChartSection {
  title: string
  items: ChartItem[]
}

@Component({
  selector: 'app-statistiques',
  templateUrl: './statistiques.component.html',
  styleUrls: ['./statistiques.component.scss'],
  standalone: true,
  imports: [CommonModule, NgIf, NgForOf, AsyncPipe, FormsModule],
})
export class StatistiquesComponent {
  chartMode: ChartMode = 'bar'
  filterPanelOpen = false
  selectedUserId: number | null = null
  dateDebut = ''
  dateFin = ''
  selectedTranchePuissance = ''
  selectedRegion = ''
  selectedTypologie = ''
  selectedModeValorisation = ''
  selectedLivrable = ''

  private readonly statistiqueService = inject(StatistiqueService)
  private readonly utilisateursService = inject(UtilisateursService)
  private readonly filters$ = new BehaviorSubject<StatistiquesProjetFilters>({})

  readonly utilisateurs$: Observable<UtilisateurDto[]> = this.utilisateursService.getAllUtilisateurs()
  readonly filtresDisponibles$: Observable<StatistiquesFiltresResponse> = this.statistiqueService
    .getFiltresDisponibles()
    .pipe(shareReplay(1))
  readonly statistiques$: Observable<StatistiquesProjetResponse> = this.filters$.pipe(
    switchMap((filters) => this.statistiqueService.getStatistiquesProjets(filters))
  )

  private readonly chartColors = ['#FF5A36', '#45D26B', '#00C8FF', '#FED83E', '#974614', '#73008F', '#2E2836', '#7C8A92']

  setChartMode(mode: ChartMode): void {
    this.chartMode = mode
  }

  toggleFilterPanel(): void {
    this.filterPanelOpen = !this.filterPanelOpen
  }

  applyFilters(): void {
    this.filters$.next({
      userId: this.selectedUserId,
      dateDebut: this.dateDebut || null,
      dateFin: this.dateFin || null,
      tranchePuissance: this.selectedTranchePuissance || null,
      region: this.selectedRegion || null,
      typologie: this.selectedTypologie || null,
      modeValorisation: this.selectedModeValorisation || null,
      livrable: this.selectedLivrable || null,
    })
  }

  resetFilters(): void {
    this.selectedUserId = null
    this.dateDebut = ''
    this.dateFin = ''
    this.selectedTranchePuissance = ''
    this.selectedRegion = ''
    this.selectedTypologie = ''
    this.selectedModeValorisation = ''
    this.selectedLivrable = ''
    this.filters$.next({})
  }

  buildSections(statistiques: StatistiquesProjetResponse): ChartSection[] {
    return [
      {
        title: 'Par tranche de puissance',
        items: this.withColors(
          statistiques.projetsParTranchePuissance?.map((item) => ({
            label: item.libelle,
            value: Number(item.nombreProjets ?? 0),
          })) ?? []
        ),
      },
      {
        title: 'Par typologie',
        items: this.withColors(
          statistiques.projetsParTypologie?.map((item) => ({
            label: item.libelle,
            value: Number(item.nombreProjets ?? 0),
          })) ?? []
        ),
      },
      {
        title: 'Par mode de valorisation',
        items: this.withColors(
          statistiques.projetsParModeValorisation?.map((item) => ({
            label: item.libelle,
            value: Number(item.nombreProjets ?? 0),
          })) ?? []
        ),
      },
      {
        title: 'Par région',
        items: this.withColors(
          statistiques.projetsParRegion?.map((item) => ({
            label: item.region || 'Non renseignée',
            value: Number(item.nombreProjets ?? 0),
          })) ?? []
        ),
      },
      {
        title: 'Par utilisateur',
        items: this.withColors(
          statistiques.projetsParUtilisateur?.map((item) => ({
            label: item.libelle || 'Non renseigné',
            value: Number(item.nombreProjets ?? 0),
          })) ?? []
        ),
      },
    ]
  }

  getTotal(items: ChartItem[]): number {
    return items.reduce((total, item) => total + item.value, 0)
  }

  getPercent(item: ChartItem, items: ChartItem[]): number {
    const total = this.getTotal(items)
    return total > 0 ? Math.round((item.value / total) * 100) : 0
  }

  getBarWidth(item: ChartItem, items: ChartItem[]): number {
    const max = Math.max(...items.map((chartItem) => chartItem.value), 0)
    return max > 0 ? Math.max((item.value / max) * 100, 4) : 0
  }

  getPieGradient(items: ChartItem[]): string {
    const total = this.getTotal(items)
    if (total === 0) {
      return '#e9ecef'
    }

    let start = 0
    const segments = items.map((item) => {
      const end = start + (item.value / total) * 100
      const segment = `${item.color} ${start}% ${end}%`
      start = end
      return segment
    })

    return `conic-gradient(${segments.join(', ')})`
  }

  private withColors(items: Array<Omit<ChartItem, 'color'>>): ChartItem[] {
    return items.map((item, index) => ({
      ...item,
      color: this.chartColors[index % this.chartColors.length],
    }))
  }
}
